import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router'

import { PageHeader } from '@/components/PageHeader'
import { fetchBoard } from '@/features/boards/api'
import { BoardCard } from '@/features/boards/BoardCard'
import { BoardColumn } from '@/features/boards/BoardColumn'
import { ProjectGuestsPanel } from '@/features/access/ProjectGuestsPanel'
import { fetchProjects } from '@/features/portfolio/api'
import { moveWorkItem } from '@/features/work-items/api'
import type { ApiError } from '@/lib/api/client'
import type { Project } from '@/types/domain'
import { useLiveProject } from '@/lib/realtime/useLiveProject'
import type { Board, WorkItem } from '@/types/work'

const COLUMN_PREFIX = 'column:'

/**
 * @param projectKey  Set when the board is rendered inside a project's view
 *                    tabs; left out on the standalone /boards route, where the
 *                    project comes from the URL instead.
 * @param embedded    Drops the page furniture — heading, project picker — that
 *                    the surrounding screen already provides.
 */
export function BoardPage({
  projectKey: fixedKey,
  embedded = false,
}: {
  projectKey?: string
  embedded?: boolean
} = {}) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  // The board lives at /boards/:projectKey so a reload, a bookmark or a link
  // pasted to a colleague all open the same board.
  const { projectKey: routeKey } = useParams()
  const projectKey = fixedKey ?? routeKey
  const [dragging, setDragging] = useState<WorkItem | null>(null)
  const [showGuests, setShowGuests] = useState(false)

  const projects = useQuery<Project[], ApiError>({
    queryKey: ['projects'],
    queryFn: () => fetchProjects(),
    enabled: !embedded,
    retry: false,
  })

  const activeKey = projectKey ?? projects.data?.[0]?.key ?? null

  const board = useQuery<Board, ApiError>({
    queryKey: ['board', activeKey],
    queryFn: () => fetchBoard(activeKey as string),
    enabled: activeKey !== null,
    retry: false,
  })

  // Two people on the same board see each other work (FR-W7).
  useLiveProject(activeKey)

  const move = useMutation({
    mutationFn: (input: { id: string; stateId: string; afterId?: string; beforeId?: string }) =>
      moveWorkItem(input.id, {
        stateId: input.stateId,
        afterId: input.afterId,
        beforeId: input.beforeId,
      }),
    // The card has already moved on screen; refetch to reconcile the rank the
    // server actually assigned.
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['board', activeKey] }),
  })

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  )

  const cardsByColumn = useMemo(() => {
    const map = new Map<string, WorkItem[]>()
    for (const column of board.data?.columns ?? []) {
      map.set(column.id, column.items)
    }
    return map
  }, [board.data?.columns])

  const columns = board.data?.columns ?? []

  function columnOf(cardId: string): string | undefined {
    for (const [columnId, items] of cardsByColumn) {
      if (items.some((item) => item.id === cardId)) return columnId
    }
    return undefined
  }

  function handleDragStart(event: DragStartEvent) {
    const id = String(event.active.id)
    const column = columnOf(id)
    setDragging(cardsByColumn.get(column ?? '')?.find((item) => item.id === id) ?? null)
  }

  function handleDragEnd(event: DragEndEvent) {
    setDragging(null)

    const { active, over } = event
    if (over === null) return

    const cardId = String(active.id)
    const overId = String(over.id)

    // Dropping on a column drops at its end; dropping on a card drops in that
    // card's place.
    const targetColumn = overId.startsWith(COLUMN_PREFIX)
      ? overId.slice(COLUMN_PREFIX.length)
      : columnOf(overId)

    if (targetColumn === undefined) return

    const target = cardsByColumn.get(targetColumn) ?? []
    const withoutDragged = target.filter((item) => item.id !== cardId)

    const index = overId.startsWith(COLUMN_PREFIX)
      ? withoutDragged.length
      : withoutDragged.findIndex((item) => item.id === overId)

    const at = index === -1 ? withoutDragged.length : index

    // The server needs the neighbours, not an index: that is what lets it
    // compute a fractional rank and write exactly one row.
    const afterId = at > 0 ? withoutDragged[at - 1]?.id : undefined
    const beforeId = withoutDragged[at]?.id

    const from = columnOf(cardId)
    if (from === targetColumn && afterId === undefined && beforeId === undefined) return

    move.mutate({
      id: cardId,
      stateId: targetColumn,
      ...(afterId === undefined ? {} : { afterId }),
      ...(beforeId === undefined ? {} : { beforeId }),
    })
  }

  if (!embedded && projects.isPending) {
    return <p className="text-ink-500 font-mono text-xs">{t('common.loading')}</p>
  }

  if (activeKey === null) {
    return (
      <div>
        <PageHeader title={t('nav.boards')} />
        <p className="border-line bg-surface text-ink-500 rounded-md border px-4 py-6 text-sm">
          {t('board.noProjects')}
        </p>
      </div>
    )
  }

  return (
    <div>
      {embedded ? null : (
      <PageHeader
        title={t('nav.boards')}
        subtitle={
          board.data === undefined ? undefined : (
            <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <Link
                to={`/projects/${board.data.project.key}`}
                className="text-brand-600 font-mono text-xs underline"
              >
                {board.data.project.key}
              </Link>
              <span>{board.data.project.name}</span>
              {board.data.project.workflow === null ? null : (
                <span className="text-ink-500 text-xs">
                  {t('workflow.delivery')}: {board.data.project.workflow.name}
                </span>
              )}
            </span>
          )
        }
      />
      )}

      <div className="mb-6 flex flex-wrap items-center gap-3">
        {embedded ? null : (
        <label className="text-ink-700 text-sm">
          <span className="me-2">{t('board.project')}</span>
          <select
            value={activeKey}
            onChange={(event) => navigate(`/boards/${event.target.value}`)}
            className="border-line bg-surface rounded-md border px-3 py-1.5 text-sm"
          >
            {(projects.data ?? []).map((project) => (
              <option key={project.id} value={project.key}>
                {project.key} — {project.name}
              </option>
            ))}
          </select>
        </label>
        )}

        <button
          type="button"
          onClick={() => setShowGuests((open) => !open)}
          className="border-line bg-surface hover:bg-surface-2 rounded-md border px-3 py-1.5 text-sm"
          aria-expanded={showGuests}
        >
          {t('access.guestAccess')}
        </button>
      </div>

      {showGuests ? <ProjectGuestsPanel projectKey={activeKey} /> : null}

      {board.isPending ? (
        <p className="text-ink-500 font-mono text-xs">{t('common.loading')}</p>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="flex gap-4 overflow-x-auto pb-4">
            {columns.map((column) => (
              <SortableContext
                key={column.id}
                items={column.items.map((item) => item.id)}
                strategy={verticalListSortingStrategy}
              >
                <BoardColumn
                  column={column}
                  droppableId={`${COLUMN_PREFIX}${column.id}`}
                  projectId={board.data?.project.id}
                />
              </SortableContext>
            ))}
          </div>

          <DragOverlay>
            {dragging ? <BoardCard item={dragging} overlay /> : null}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  )
}
